import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  styled
} from '@mui/material';

// Mock data generator
const generateMockData = () => {
  const mockContacts = [];
  
  // TikTok users (14)
  const tiktokNames = [
    'María González', 'Carlos Rodríguez', 'Ana Martínez', 'Luis Fernández',
    'Sofia López', 'Diego Sánchez', 'Laura García', 'Javier Pérez',
    'Carmen Ruiz', 'Miguel Torres', 'Elena Díaz', 'Roberto Morales',
    'Isabel Jiménez', 'Fernando Castro'
  ];
  const tiktokUsernames = [
    'maria_gonzalez_ok', 'carlos_rodriguez_tk', 'ana_martinez_vids', 'luis_fernandez_show',
    'sofia_lopez_tiktok', 'diego_sanchez_clips', 'laura_garcia_viral', 'javier_perez_trend',
    'carmen_ruiz_fun', 'miguel_torres_short', 'elena_diaz_crea', 'roberto_morales_tk',
    'isabel_jimenez_vids', 'fernando_castro_clips'
  ];
  
  // Email/Web users (10)
  const emailNames = [
    'Patricia Moreno', 'Ricardo Vega', 'Adriana Herrera', 'Andrés Mendoza',
    'Gabriela Rojas', 'Francisco Silva', 'Valentina Campos', 'Sebastián Vargas',
    'Daniela Peña', 'Alejandro Medina'
  ];
  
  // LinkedIn users (20)
  const linkedinNames = [
    'Juan Carlos Ramírez', 'María Elena Vargas', 'Roberto Andrés Soto', 'Carolina Beatriz Muñoz',
    'Fernando José Delgado', 'Andrea Patricia Núñez', 'Luis Miguel Ortega', 'Paola Alejandra Ríos',
    'Sergio Antonio Paredes', 'Natalia Constanza Flores', 'Rodrigo Esteban Guzmán', 'Camila Ignacia Salazar',
    'Felipe Sebastián Cáceres', 'Javiera Francisca Tapia', 'Matías Ignacio Espinoza', 'Constanza Antonia Bravo',
    'Ignacio Andrés Valdés', 'Francisca Belén Sepúlveda', 'Tomás Eduardo Contreras', 'Amanda Paz Fuentes'
  ];
  const linkedinUsernames = [
    'juan-ramirez-tech', 'maria-vargas-lead', 'roberto-soto-exec', 'carolina-munoz-biz',
    'fernando-delgado-pro', 'andrea-nunez-sales', 'luis-ortega-consult', 'paola-rios-marketing',
    'sergio-paredes-ceo', 'natalia-flores-hr', 'rodrigo-guzman-cto', 'camila-salazar-cfo',
    'felipe-caceres-founder', 'javiera-tapia-director', 'matias-espinoza-vp', 'constanza-bravo-manager',
    'ignacio-valdes-senior', 'francisca-sepulveda-lead', 'tomas-contreras-head', 'amanda-fuentes-director'
  ];
  
  // Generate TikTok contacts
  tiktokNames.forEach((name, index) => {
    const parts = name.split(' ');
    const email = `${parts[0].toLowerCase()}.${parts[1].toLowerCase()}@${['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'][index % 4]}`;
    const isResponded = index % 2 === 0; // 50% responded
    
    mockContacts.push({
      id: `tiktok-${index + 1}`,
      name,
      email,
      source: 'tiktok',
      tiktok_username: tiktokUsernames[index],
      linkedin_username: null,
      status: 'lead',
      created_at: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      responded: isResponded
    });
  });
  
  // Generate Email/Web contacts
  emailNames.forEach((name, index) => {
    const parts = name.split(' ');
    const email = `${parts[0].toLowerCase()}.${parts[1].toLowerCase()}@${['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com'][index % 5]}`;
    const isResponded = index % 2 === 1; // 50% responded
    
    mockContacts.push({
      id: `email-${index + 1}`,
      name,
      email,
      source: 'web',
      tiktok_username: null,
      linkedin_username: null,
      status: 'lead',
      created_at: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      responded: isResponded
    });
  });
  
  // Generate LinkedIn contacts
  linkedinNames.forEach((name, index) => {
    const parts = name.split(' ');
    const email = `${parts[0].toLowerCase()}.${parts[parts.length - 1].toLowerCase()}@${['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'linkedin.com'][index % 5]}`;
    const isResponded = index % 2 === 0; // 50% responded
    
    mockContacts.push({
      id: `linkedin-${index + 1}`,
      name,
      email,
      source: 'linkedin',
      tiktok_username: null,
      linkedin_username: linkedinUsernames[index],
      status: 'lead',
      created_at: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      responded: isResponded
    });
  });
  
  return mockContacts;
};

const CrmDashboard = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [n8nEnabled, setN8nEnabled] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const storedToken = localStorage.getItem('crm_token');
    if (!storedToken) {
      navigate('/login');
      return;
    }
    
    // Load mock data
    setTimeout(() => {
      const mockData = generateMockData();
      setContacts(mockData);
      setLoading(false);
    }, 500);
  }, [navigate]);

  useEffect(() => {
    // Filter contacts based on activeFilter
    const mockData = generateMockData();
    if (activeFilter === 'all') {
      setContacts(mockData);
    } else {
      setContacts(mockData.filter(c => c.source === activeFilter));
    }
  }, [activeFilter]);

  const handleLogout = () => {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    navigate('/login');
  };

  const handleN8nToggle = (checked) => {
    setN8nEnabled(checked);
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'linkedin':
        return 'primary';
      case 'tiktok':
        return 'secondary';
      case 'web':
        return 'success';
      default:
        return 'default';
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'linkedin':
        return 'LinkedIn';
      case 'tiktok':
        return 'TikTok';
      case 'web':
        return 'Email';
      default:
        return source;
    }
  };

  const DashboardContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  }));

  const HeaderBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  }));

  const FilterBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  }));

  const filteredContacts = activeFilter === 'all' 
    ? contacts 
    : contacts.filter(c => c.source === activeFilter);

  return (
    <>
      <Helmet>
        <title>Lead Intelligence Hub - PeopleForTech CRM</title>
        <meta name="description" content="CRM Dashboard for PeopleForTech" />
      </Helmet>
      <DashboardContainer maxWidth="xl">
        <HeaderBox>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
            Lead Intelligence Hub - PeopleForTech CRM
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button variant="contained" onClick={() => navigate('/crm/reservas')} sx={{ backgroundColor: '#000', '&:hover': { backgroundColor: '#333' } }}>
              Calendario reservas
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={n8nEnabled}
                  onChange={(e) => handleN8nToggle(e.target.checked)}
                  color="primary"
                />
              }
              label="Respuesta automática con n8n"
            />
            <Button variant="outlined" color="error" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </HeaderBox>

        <FilterBox>
          <Button
            variant={activeFilter === 'all' ? 'contained' : 'outlined'}
            onClick={() => setActiveFilter('all')}
          >
            All ({contacts.length})
          </Button>
          <Button
            variant={activeFilter === 'linkedin' ? 'contained' : 'outlined'}
            onClick={() => setActiveFilter('linkedin')}
            color="primary"
          >
            LinkedIn ({contacts.filter(c => c.source === 'linkedin').length})
          </Button>
          <Button
            variant={activeFilter === 'web' ? 'contained' : 'outlined'}
            onClick={() => setActiveFilter('web')}
            color="success"
          >
            Email/Web ({contacts.filter(c => c.source === 'web').length})
          </Button>
          <Button
            variant={activeFilter === 'tiktok' ? 'contained' : 'outlined'}
            onClick={() => setActiveFilter('tiktok')}
            color="secondary"
          >
            TikTok ({contacts.filter(c => c.source === 'tiktok').length})
          </Button>
        </FilterBox>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Source</strong></TableCell>
                  <TableCell><strong>LinkedIn</strong></TableCell>
                  <TableCell><strong>TikTok</strong></TableCell>
                  <TableCell><strong>Respondido</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
                        No contacts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredContacts.map((contact) => (
                    <TableRow key={contact.id} hover>
                      <TableCell>{contact.name}</TableCell>
                      <TableCell>{contact.email || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={getSourceLabel(contact.source)}
                          color={getSourceColor(contact.source)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {contact.linkedin_username ? (
                          <Typography variant="body2" color="primary">
                            @{contact.linkedin_username}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.tiktok_username ? (
                          <Typography variant="body2" color="secondary">
                            @{contact.tiktok_username}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={contact.responded ? 'Respondido' : 'Sin respuesta'}
                          color={contact.responded ? 'success' : 'error'}
                          size="small"
                          sx={{
                            backgroundColor: contact.responded ? '#4caf50' : '#f44336',
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={contact.status || 'lead'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {contact.created_at
                          ? new Date(contact.created_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && filteredContacts.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Showing {filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </DashboardContainer>
    </>
  );
};

export default CrmDashboard;
